/**
 * PATH: apps/qa/src/pages/reports/report-helpers.ts
 * PURPOSE: Type definitions and helper functions for the Reports page
 * WHY: Extracted from ReportsPage.tsx to keep files under 300 lines
 */

import {
  FileText,
  CheckCircle,
  FolderOpen,
  Package,
} from 'lucide-react'

export type ReportType = 'inspection' | 'compliance' | 'ncr_summary' | 'qa_pack'
export type ReportStatus = 'generating' | 'ready' | 'error' | 'expired'

export const typeOptions = [
  { value: '', label: 'All Types' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'area_report', label: 'Area Report' },
  { value: 'ncr_pack', label: 'NCR Summary' },
  { value: 'qa_pack', label: 'QA Pack' },
  { value: 'monthly_pack', label: 'Monthly Pack' },
  { value: 'itp_report', label: 'ITP Report' },
]

/** The reports this page can generate. The PDF builder uses the same values. */
export const STANDARD_REPORTS = [
  { value: 'inspection_report', label: 'Inspection Report', detail: 'Checklist results, hold points, and sign-off for one inspection.' },
  { value: 'area_report', label: 'Area Report', detail: 'Areas and checklist rows, without the photo gallery.' },
  { value: 'ncr_pack', label: 'NCR Summary', detail: 'Open and closed non-conformances for the inspection.' },
  { value: 'qa_pack', label: 'QA Pack', detail: 'Summary, checklist, non-conformances, photos, and sign-off.' },
  { value: 'monthly_pack', label: 'Monthly Pack', detail: 'Current progress, checklist, and non-conformances.' },
  { value: 'itp_report', label: 'ITP Report', detail: 'The inspection test plan for the checks on this inspection.' },
] as const

export function getTypeIcon(type: ReportType) {
  switch (type) {
    case 'inspection':
      return FileText
    case 'compliance':
      return CheckCircle
    case 'ncr_summary':
      return FolderOpen
    case 'qa_pack':
      return Package
    default:
      return FileText
  }
}

export function getTypeStyles(type: ReportType) {
  const styles = {
    inspection: { 
      bg: 'bg-blue-100 dark:bg-blue-900/30', 
      text: 'text-blue-700 dark:text-blue-400',
      icon: 'text-blue-600 dark:text-blue-400'
    },
    compliance: { 
      bg: 'bg-emerald-100 dark:bg-emerald-900/30', 
      text: 'text-emerald-700 dark:text-emerald-400',
      icon: 'text-emerald-600 dark:text-emerald-400'
    },
    ncr_summary: { 
      bg: 'bg-amber-100 dark:bg-amber-900/30', 
      text: 'text-amber-700 dark:text-amber-400',
      icon: 'text-amber-600 dark:text-amber-400'
    },
    qa_pack: { 
      bg: 'bg-purple-100 dark:bg-purple-900/30', 
      text: 'text-purple-700 dark:text-purple-400',
      icon: 'text-purple-600 dark:text-purple-400'
    },
  }
  return styles[type] || styles.inspection
}

export function getStatusStyles(status: string) {
  const styles: Record<string, { bg: string; text: string }> = {
    queued: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
    generating: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
    generated: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    ready: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
    error: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
    expired: { bg: 'bg-muted', text: 'text-muted-foreground' },
  }
  return styles[status] || styles.generated
}

export function getStatusLabel(status: string) {
  const labels: Record<string, string> = {
    queued: 'Queued',
    generating: 'Generating',
    generated: 'Ready',
    ready: 'Ready',
    failed: 'Failed',
    error: 'Error',
  }
  return labels[status] || status
}
